// Copyright (C) 2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    CloseOutlined, DownOutlined, EyeOutlined, ReloadOutlined, SaveOutlined,
} from '@ant-design/icons';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Dropdown from 'antd/lib/dropdown';
import Form, { FormInstance } from 'antd/lib/form';
import Menu from 'antd/lib/menu';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import Tag from 'antd/lib/tag';
import Typography from 'antd/lib/typography';

import {
    AiGradeProvider, AiGradeResult, AiGradeSource, AiGradesState, CombinedState,
} from 'reducers/interfaces';
import { setGradesFormState, collapseSidebar } from 'actions/annotation-actions';
import {
    gradesActions,
    loadingGradesAsync,
    submitAnnotationFrameToGradeAsync,
    submitHumanGradesAsync,
    updateTaskMeta,
    GenerateMode,
} from 'actions/grades-actions';
import {
    AiCard, aiGradesActions, fetchAiGradeSources, loadAiModelsAsync, loadAiSpendAsync, pickAiModel,
    runAiModelAsync, watchAiGradesAsync,
} from 'actions/ai-grades-actions';
import CVATTooltip from 'components/common/cvat-tooltip';
import { parseFilename } from 'utils/grades';
import { AI_GRADE_SOURCES, EMPTY_AI_GRADES } from 'utils/ai-grades';
import GradesTable, { RowVisibility } from './grades-table';

type GradeValues = CombinedState['grades']['values'];

const GENERATE_MENU_ITEMS: { key: GenerateMode; label: string }[] = [
    { key: 'grades', label: 'Generate RoboGrades' },
    { key: 'masks', label: 'Generate Masks' },
    { key: 'grades_and_masks', label: 'Generate RoboGrades & Masks' },
    { key: 'grades_and_masks_and_human_grades', label: 'Generate RoboGrades & Masks + Human Grades' },
];

const ROW_OPTIONS: { key: keyof RowVisibility; label: string }[] = [
    { key: 'enhanced', label: 'Enhanced RoboGrades' },
    { key: 'robogrades', label: 'RoboGrades' },
    { key: 'predicted', label: 'Predicted RoboGrade' },
    { key: 'ai', label: 'AI reference grades' },
];

// messages arrive from three APIs, some as HTML error pages; the panel shows one line of text
const plainMessage = (message: string | Error): string => (
    (typeof message === 'string' ? message : message.message)
        .replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 300)
);

function IdField({
    label, value, onChange,
}: {
    label: string;
    value: string;
    onChange?(value: string): void;
}): JSX.Element {
    return (
        <div className='grades-form-id'>
            <span className='grades-form-id-label'>{label}</span>
            <Typography.Text
                className='grades-form-id-value'
                editable={onChange ? { onChange, tooltip: `Edit the ${label.toLowerCase()}` } : false}
            >
                {value || '—'}
            </Typography.Text>
        </div>
    );
}

interface Props {
    // the job's task: the card ids live on it
    task: any;
}

export function GradesForm({ task }: Props): JSX.Element | null {
    const dispatch = useDispatch();
    const formRef = useRef<FormInstance<GradeValues>>(null);
    const {
        open, jobInstance, frameNumber, frameFilename, isGradesLoading, isGeneratingRobogrades,
        isUpdatingHumanGrades, error, warning, values, ai, aiModels, aiSpend,
    } = useSelector((state: CombinedState) => ({
        open: state.annotation.gradesFrom.open,
        jobInstance: state.annotation.job.instance,
        frameNumber: state.annotation.player.frame.number,
        frameFilename: state.annotation.player.frame.filename,
        isGradesLoading: state.grades.grades_loading,
        isGeneratingRobogrades: state.grades.generate_robogrades_loading,
        isUpdatingHumanGrades: state.grades.update_human_grades_loading,
        error: state.grades.error,
        warning: state.grades.warning,
        values: state.grades.values,
        ai: state.grades.ai,
        aiModels: state.grades.aiModels,
        aiSpend: state.grades.aiSpend,
    }), shallowEqual);
    const [orderId, setOrderId] = useState<string>(task?.orderId || '');
    const [certificateId, setCertificateId] = useState<string>(task?.certificateId || '');
    const [liveValues, setLiveValues] = useState<Partial<GradeValues>>(values);
    const [visibility, setVisibility] = useState<RowVisibility>({
        enhanced: true, robogrades: true, predicted: true, ai: true,
    });

    useEffect(() => {
        setOrderId((prev) => prev || task?.orderId || '');
    }, [task?.orderId]);

    useEffect(() => {
        setCertificateId((prev) => prev || task?.certificateId || '');
    }, [task?.certificateId]);

    const frameOptions = useMemo(() => {
        const options = parseFilename(frameFilename);
        if (orderId) {
            options.orderId = orderId;
        }
        if (certificateId) {
            options.certificateId = certificateId;
        }
        return options;
    }, [frameFilename, orderId, certificateId]);

    const handleClose = useCallback((): void => {
        dispatch(setGradesFormState(false));
        dispatch(collapseSidebar());
    }, [dispatch]);

    const handleGenerate = useCallback((mode: GenerateMode): void => {
        dispatch(submitAnnotationFrameToGradeAsync({ ...frameOptions, generateMode: mode }));
    }, [dispatch, frameOptions]);

    const handleReload = useCallback((): void => {
        dispatch(loadingGradesAsync(frameOptions.certificateId));
    }, [dispatch, frameOptions.certificateId]);

    const handleUpdateEnhancedRobogrades = useCallback(async (): Promise<void> => {
        const formValues = await formRef.current?.validateFields();
        if (formValues) {
            dispatch(gradesActions.setGrades(formValues));
            dispatch(submitHumanGradesAsync(frameOptions.certificateId));
        }
    }, [dispatch, frameOptions]);

    // antd calls onChange once, when the edit is confirmed, with the new value
    const handleIdChange = useCallback((key: 'orderId' | 'certificateId', value: string): void => {
        const ids = { orderId, certificateId, [key]: value };
        if (key === 'orderId') {
            setOrderId(value);
        } else {
            setCertificateId(value);
        }
        if (task && (ids.orderId !== (task.orderId || '') || ids.certificateId !== (task.certificateId || ''))) {
            dispatch(updateTaskMeta(task, ids));
        }
    }, [dispatch, task, orderId, certificateId]);

    // The AI grades are the card's, keyed by its certificate: the one in the frame's name first, as
    // the server reads it (a task may hold several cards), else the panel's.
    const aiCertificateId = parseFilename(frameFilename).certificateId ||
        (frameOptions.certificateId ? String(frameOptions.certificateId) : '') || null;
    let aiDisabledReason: string | null = null;
    if (!jobInstance || !aiCertificateId) {
        aiDisabledReason = 'The frame does not name a certificate';
    } else if (frameOptions.imageType !== 'laser') {
        aiDisabledReason = 'AI grades are made from the laser scans of a card';
    } else if (!aiModels.loaded) {
        aiDisabledReason = 'Loading the AI settings';
    } else if (!aiModels.providers) {
        aiDisabledReason = 'The AI settings could not be loaded';
    } else if (!aiModels.canGenerate) {
        aiDisabledReason = 'Only administrators and reviewers generate AI grades';
    }

    // The sources this card has on both sides: a run grades all of them, and the table says which
    // the card lacks. Until the server has answered, all are assumed.
    const [aiSources, setAiSources] = useState<AiGradeSource[]>(AI_GRADE_SOURCES);
    const jobId: number | null = jobInstance ? jobInstance.id : null;
    useEffect(() => {
        setAiSources(AI_GRADE_SOURCES);
        if (!open || jobId === null || !aiCertificateId) {
            return undefined;
        }

        let cancelled = false;
        fetchAiGradeSources(jobId, frameNumber).then((sources) => {
            if (!cancelled) {
                setAiSources(sources);
            }
        }).catch(() => {
            // the hint about missing sources is left out
        });

        return () => {
            cancelled = true;
        };
    }, [open, jobId, aiCertificateId]);

    const aiCard = useMemo((): AiCard | null => (
        jobId !== null && aiCertificateId ? { jobId, frame: frameNumber, certificateId: aiCertificateId } : null
    ), [jobId, frameNumber, aiCertificateId]);

    // Another card on screen starts empty; the card's grades are read, its first run is started if
    // it has none (the server decides, once per card), and the rows are polled while busy.
    useEffect(() => {
        if (!open || !aiCard) {
            return;
        }
        if (aiCard.certificateId !== ai.certificateId) {
            dispatch(aiGradesActions.reset(aiCard.certificateId));
        }
        dispatch(watchAiGradesAsync(aiCard, frameOptions.imageType === 'laser'));
    }, [open, aiCard?.jobId, aiCard?.certificateId]);

    const handleGenerateAi = useCallback((provider: AiGradeProvider): void => {
        if (aiCard) {
            dispatch(runAiModelAsync(aiCard, provider));
        }
    }, [dispatch, aiCard]);
    const handleRetryAi = useCallback((result: AiGradeResult): void => {
        if (aiCard) {
            dispatch(runAiModelAsync(aiCard, result.provider, {
                sources: [result.source], pick: { model: result.model, effort: result.effort },
            }));
        }
    }, [dispatch, aiCard]);
    const handleSelectAiModel = useCallback((provider: AiGradeProvider, model: string): void => {
        dispatch(pickAiModel(provider, { model }));
    }, [dispatch]);
    const handleSelectAiEffort = useCallback((provider: AiGradeProvider, effort: string): void => {
        dispatch(pickAiModel(provider, { effort }));
    }, [dispatch]);

    // the models, loaded the first time the panel opens; the spend, each time an admin opens it
    useEffect(() => {
        if (open && !aiModels.loaded && !aiModels.loading) {
            dispatch(loadAiModelsAsync());
        } else if (open && aiModels.canSeeSpend) {
            dispatch(loadAiSpendAsync());
        }
    }, [open]);

    const aiState = useMemo((): AiGradesState => {
        if (!aiCertificateId) {
            return { ...EMPTY_AI_GRADES, loaded: true, error: 'This frame does not name a certificate' };
        }
        // the store may still hold the last card's grades
        return ai.certificateId === aiCertificateId ? ai : EMPTY_AI_GRADES;
    }, [ai, aiCertificateId]);

    useEffect(() => {
        if (open) {
            dispatch(loadingGradesAsync(frameOptions.certificateId));
        }
    }, [open, frameOptions.certificateId]);

    useEffect(() => {
        formRef.current?.setFieldsValue(values);
        setLiveValues(values);
    }, [values, open]);

    if (!open) {
        return null;
    }

    const rowsPicker = (
        <div className='grades-form-rows'>
            {ROW_OPTIONS.map(({ key, label }) => (
                <Checkbox
                    key={key}
                    checked={visibility[key]}
                    onChange={(event) => setVisibility((prev) => ({ ...prev, [key]: event.target.checked }))}
                >
                    {label}
                </Checkbox>
            ))}
        </div>
    );

    const generateMenu = (
        <Menu onClick={({ key }): void => handleGenerate(key as GenerateMode)}>
            {GENERATE_MENU_ITEMS.map(({ key, label }) => (
                <Menu.Item key={key}>{label}</Menu.Item>
            ))}
        </Menu>
    );

    return (
        <div className='grades-form'>
            <header className='grades-form-header'>
                <div className='grades-form-identity'>
                    <Typography.Title level={5} className='grades-form-title'>Grades</Typography.Title>
                    <div className='grades-form-ids'>
                        <IdField
                            label='Certificate'
                            value={String(frameOptions.certificateId || '')}
                            onChange={(value: string) => handleIdChange('certificateId', value)}
                        />
                        <IdField
                            label='Order'
                            value={String(frameOptions.orderId || '')}
                            onChange={(value: string) => handleIdChange('orderId', value)}
                        />
                        {frameOptions.cardName ? <IdField label='Card' value={frameOptions.cardName} /> : null}
                        {frameOptions.orientation ? (
                            <div className='grades-form-id'>
                                <span className='grades-form-id-label'>On screen</span>
                                <span>
                                    <Tag color='blue'>{frameOptions.orientation}</Tag>
                                    {frameOptions.imageType ? <Tag>{frameOptions.imageType}</Tag> : null}
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className='grades-form-actions'>
                    <Popover content={rowsPicker} trigger='click' placement='bottomRight' title='Rows'>
                        <Button icon={<EyeOutlined />}>Rows</Button>
                    </Popover>
                    <CVATTooltip title='Reload the grades of this card'>
                        <Button icon={<ReloadOutlined />} aria-label='Reload grades' onClick={handleReload} />
                    </CVATTooltip>
                    <Dropdown overlay={generateMenu}>
                        <Button loading={isGeneratingRobogrades}>
                            Generate
                            <DownOutlined />
                        </Button>
                    </Dropdown>
                    <Button
                        type='primary'
                        icon={<SaveOutlined />}
                        loading={isUpdatingHumanGrades}
                        onClick={handleUpdateEnhancedRobogrades}
                    >
                        Update Enhanced RoboGrades
                    </Button>
                    <CVATTooltip title='Close the grades panel'>
                        <Button
                            type='text'
                            icon={<CloseOutlined />}
                            aria-label='Close the grades panel'
                            onClick={handleClose}
                            className='grades-form-close'
                        />
                    </CVATTooltip>
                </div>
            </header>

            {error ? (
                <Alert className='grades-form-alert' type='error' showIcon message={plainMessage(error)} />
            ) : null}
            {warning ? (
                <Alert className='grades-form-alert' type='warning' showIcon message={plainMessage(warning)} />
            ) : null}

            <Spin spinning={isGradesLoading}>
                <Form
                    ref={formRef}
                    name='grades_form'
                    initialValues={values}
                    onValuesChange={(_, all: Partial<GradeValues>) => setLiveValues(all)}
                >
                    <GradesTable
                        values={values}
                        liveValues={liveValues}
                        visibility={visibility}
                        ai={aiState}
                        aiSources={aiSources}
                        aiDisabledReason={aiDisabledReason}
                        aiModels={aiModels}
                        aiSpend={aiSpend}
                        onGenerateAi={handleGenerateAi}
                        onRetryAi={handleRetryAi}
                        onSelectAiModel={handleSelectAiModel}
                        onSelectAiEffort={handleSelectAiEffort}
                    />
                </Form>
            </Spin>
        </div>
    );
}

export default GradesForm;
